import { isEmpty, isNull, isUndefined } from 'underscore';
import { ToPx } from 'components/VideoTemplateEditor/types';
import { CaptionsOverride } from 'types';
import { Measurement, ViewportWidth } from 'utils/measurement';
import { CaptionsState, VideoTemplateStateContent } from '../../types';

const getCaptionsPositionInPx = (
  captions: CaptionsState,
  toPx: ToPx,
): CaptionsOverride['position'] => {
  return {
    left: toPx(captions.position?.left)?.value,
    top: toPx(captions.position?.top)?.value,
  };
};

const getCaptionsSizeInPx = (
  captions: CaptionsState,
  toPx: ToPx,
): CaptionsOverride['size'] => {
  return {
    height: toPx(captions.size?.height).value,
    width: toPx(captions.size?.width).value,
  };
};

const getCaptionsFontSizeInPx = (
  captions: CaptionsState,
  toPx: ToPx,
): CaptionsOverride['containerStyle']['fontSize'] => {
  const fontSizePx = toPx(
    new ViewportWidth(parseFloat(captions.containerStyle?.fontSize)),
  );

  return fontSizePx.value;
};

export const getPaddingValue = (value?: string): number | undefined => {
  const parsedValue = parseFloat(value);

  return new ViewportWidth(
    isNull(parsedValue) || isUndefined(parsedValue) || isNaN(parsedValue)
      ? 0
      : parsedValue,
  ).value;
};

export const formatCaptionsPadding = (
  containerStyle: CaptionsOverride['containerStyle'],
): CaptionsOverride['containerStyle'] => {
  return {
    ...containerStyle,
    paddingTop: getPaddingValue(containerStyle.paddingTop),
    paddingRight: getPaddingValue(containerStyle.paddingRight),
    paddingBottom: getPaddingValue(containerStyle.paddingBottom),
    paddingLeft: getPaddingValue(containerStyle.paddingLeft),
  };
};

export const getCaptionsExportConfig = (
  state: VideoTemplateStateContent,
): CaptionsOverride => {
  const { captions, canvas, transcription } = state;

  if (isEmpty(captions) || !transcription?.transcribe) {
    return undefined;
  }

  const viewport = captions.editor?.styleContext?.viewport ?? canvas;

  const toPx = (m: Measurement) => m?.toUnit('px', viewport);

  const position = getCaptionsPositionInPx(captions, toPx);
  const size = getCaptionsSizeInPx(captions, toPx);

  return {
    ...captions,
    containerStyle: formatCaptionsPadding({
      ...captions.containerStyle,
      fontSize: getCaptionsFontSizeInPx(captions, toPx),
      width: captions.size?.width?.toString(),
    }),
    enabled: transcription?.transcribe,
    hasBeenEdited: true,
    region: {
      ...captions.region,
      properties: {
        ...captions.region?.properties,
        left: captions.position?.left?.toString(),
        top: captions.position?.top?.toString(),
      },
    },
    textBoxHeight: size.height,
    position,
    size,
  };
};
